import xdk from '@accedo/xdk-core'
import isTizen from '@accedo/xdk-device-samsung-tizen/esm/detection'

import { LinkModelTypesEnum } from '../../types/flo-enums'
import {
  TvBrowseLayoutModel,
  TvEditFavoritesLayoutModel,
  TvGridLayoutModel,
  TvGridPartialModel,
  TvHomePartialModel,
  TvSearchLayoutModel,
  UserCustomization,
} from '../../types/flo-experience'
import {
  getAuthToken,
  getRefreshToken,
  isTokenExpired,
  storeUserData,
} from '../../utils/authentication'
import { generateUniqueId, logGenericDatadogError } from '../../utils/datadog'
import { getAppVersion } from '../../utils/info'
import { getCoreApiUrl } from '../../utils/network'
import { AppConfig } from '../appConfig'
import { NetworkCache } from '../networkCache'

const API_VERSION = '1.34.0'

export const composeUrl = (url: string, params?: object) => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

  params = {
    ...params,
    version: API_VERSION,
    tz: timeZone,
  }

  const queryParams = Object.keys(params)
    .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
    .join('&')
  return `${url}?${queryParams}`
}

export const getHeaders = () => {
  const token = getAuthToken()

  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'x-flo-app': `${xdk.platform}-app`,
    'x-flo-app-version': getAppVersion(),
    ...(isTizen && {
      'x-datadog-sampling-priority': '1',
      'x-datadog-origin': 'rum',
      'x-datadog-trace-id': generateUniqueId(),
      'x-datadog-parent-id': generateUniqueId(),
    }),
    ...(token && { Authorization: `Bearer ${token}` }),
  }

  return new Headers(headers)
}

export const fetchData = async ({
  endpoint,
  method,
  payload,
}: {
  endpoint: string
  method?: string
  payload?: string
}) => {
  try {
    // Check token and refresh if necessary
    const token = getAuthToken()
    if (token && isTokenExpired()) {
      const refreshToken = getRefreshToken()
      const data = await AppConfig.shared.authService.refreshToken({ token: refreshToken })

      if (data?.token && data?.refresh_token) {
        storeUserData(data)
      }
    }

    const response = await fetch(endpoint, {
      method: method ?? 'GET',
      headers: getHeaders(),
      ...(payload ? { body: payload } : {}),
    })

    if (!response.ok) {
      logGenericDatadogError(response?.status?.toString(), response?.statusText)

      return {
        error: response?.statusText,
        status: response?.status,
        isNetworkError: false,
      } as ErrorType
    }

    if (response.status === 201 || response.status === 204) {
      return true
    }

    const data = await response.json()

    if (
      ('http_status' in data && data?.http_status >= 200 && data?.http_status < 300) ||
      endpoint.includes('user-customizations')
    ) {
      return data
    } else {
      const bffError = data?.sections?.[0]?.text ?? data?.sections?.[0]?.title
      logGenericDatadogError(response?.status?.toString(), bffError)

      return {
        error: bffError,
        status: response?.status,
        isNetworkError: false,
      } as ErrorType
    }
  } catch (error) {
    logGenericDatadogError(error?.name, error?.message, error)
    console.error('Fetch failed for', endpoint, error)

    const isNetworkError =
      error instanceof TypeError &&
      (error.message === 'Failed to fetch' ||
        error.message === 'NetworkError when attempting to fetch resource.')

    return {
      error: error,
      status: isNetworkError ? 0 : -1,
      isNetworkError,
    } as ErrorType
  }
}

export class FloExperience extends NetworkCache {
  protected DOMAIN = getCoreApiUrl()
  protected EXPERIENCES_API = '/api/experiences'
  protected BASE_URL = `${this.DOMAIN}${this.EXPERIENCES_API}`

  constructor() {
    super('FloExperience')
  }

  public get homeInitialPageSize() {
    return 2
  }

  public async getHome(params?: {
    site_id?: string
    limit?: string | number
    offset?: string | number
  }): Promise<TvBrowseLayoutModel & ErrorType> {
    const endpoint = `${this.BASE_URL}/tv/home`

    return await fetchData({ endpoint: composeUrl(endpoint, params) })
  }

  public async getHomePartial(partialUrl: string): Promise<TvHomePartialModel & ErrorType> {
    const endpoint = `${this.DOMAIN}${partialUrl}`

    return await fetchData({ endpoint })
  }

  public async getSports(params?: { site_id?: string }): Promise<TvGridLayoutModel & ErrorType> {
    const endpoint = `${this.BASE_URL}/tv/sport-picker`

    return await fetchData({ endpoint: composeUrl(endpoint, params) })
  }

  public async getEventHub(path: string): Promise<TvBrowseLayoutModel & ErrorType> {
    const endpoint = `${this.DOMAIN}${path}`

    const data = await fetchData({ endpoint })
    data?.container?.items?.forEach((i) => {
      i?.items?.forEach((v) => {
        v.action.web_brackets_url = data?.content_metadata?.web_brackets_url
        v.action.web_game_center_url = data?.content_metadata?.web_game_center_url
      })
    })

    return data
  }

  public async getFavoriteHub(searchUrl: string): Promise<TvBrowseLayoutModel & ErrorType> {
    const endpoint = `${this.DOMAIN}${searchUrl}`
    return await fetchData({ endpoint })
  }

  public async getEditFavorites(url?: string): Promise<TvEditFavoritesLayoutModel & ErrorType> {
    const endpoint = url ? `${this.DOMAIN}${url}` : `${this.BASE_URL}/tv/edit-favorites`

    return await fetchData({ endpoint: url ? endpoint : composeUrl(endpoint) })
  }

  public async getNavHub(params?: {
    site_id?: string
    nav_id?: string
    limit?: string | number
    offset?: string | number
  }): Promise<TvBrowseLayoutModel & ErrorType> {
    const endpoint = `${this.BASE_URL}/tv/nav-hub`

    return await fetchData({ endpoint: composeUrl(endpoint, params) })
  }

  public async getTvGridPartial(
    url: string,
    excludePaginationItem = false,
  ): Promise<TvGridPartialModel & ErrorType> {
    const endpoint = `${this.DOMAIN}${url}`
    const data = await fetchData({ endpoint })

    if ('error' in data) return data

    if (excludePaginationItem) {
      const filteredItems = data.data.items.filter((item) => {
        return item.type !== LinkModelTypesEnum.Link
      })

      data.data.items = filteredItems
    }

    return data
  }

  public async getSearchRecommendations(): Promise<TvSearchLayoutModel & ErrorType> {
    const endpoint = `${this.BASE_URL}/tv/search/recommendations`

    return await fetchData({ endpoint: composeUrl(endpoint) })
  }

  public async getSearch(searchUrl: string): Promise<TvSearchLayoutModel & ErrorType> {
    const endpoint = `${this.DOMAIN}${searchUrl}`
    return await fetchData({ endpoint })
  }

  public async getSearchPartial(
    searchUrl: string,
    excludePaginationItem = false,
  ): Promise<TvGridPartialModel & ErrorType> {
    return this.getTvGridPartial(searchUrl, excludePaginationItem)
  }

  public async getSportsFilter(searchUrl: string): Promise<TvGridLayoutModel & ErrorType> {
    const endpoint = `${this.DOMAIN}${searchUrl}`
    return await fetchData({ endpoint: composeUrl(endpoint) })
  }

  public async getCollection(
    collectionUrl: string,
    excludePaginationItem = false,
  ): Promise<TvGridLayoutModel & ErrorType> {
    const endpoint = `${this.DOMAIN}${collectionUrl}`
    const data = await fetchData({ endpoint })

    if ('error' in data) return data

    if (excludePaginationItem) {
      const filteredItems = data.container.items.filter((item) => {
        return item.type !== LinkModelTypesEnum.Link
      })

      data.container.items = filteredItems
    }

    return data
  }

  public async getEditFavoritesPartial(tabUrl: string): Promise<TvGridPartialModel | ErrorType> {
    return this.getTvGridPartial(tabUrl)
  }

  public async getUserCustomizations(): Promise<UserCustomization[]> {
    try {
      const res = await fetchData({
        endpoint: `https://user-preferences.flokubernetes.com/v1/users/me/user-customizations`,
      })
      return res
    } catch (error) {
      console.error(error)
    }
  }
}
